/**
 * Permission Redux Slice
 * Phase 5: RBAC & Permission System - M3
 * 
 * State management for permission CRUD operations
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import permissionService from '../../services/permissionService';
import alertify from 'alertifyjs';

// ============= ASYNC THUNKS =============

/**
 * Fetch all permissions (with optional filters)
 */
export const fetchPermissions = createAsyncThunk(
  'permission/fetchPermissions',
  async (filters = {}, { rejectWithValue }) => {
    try {
      return await permissionService.getPermissions(filters);
    } catch (error) {
      const message = error.response?.data?.message || 'Yetkiler yüklenemedi';
      return rejectWithValue(message);
    }
  }
);

/**
 * Create new permission
 */
export const createPermission = createAsyncThunk(
  'permission/createPermission',
  async (permissionData, { rejectWithValue }) => {
    try {
      const permission = await permissionService.createPermission(permissionData);
      alertify.success('Yetki başarıyla oluşturuldu');
      return permission;
    } catch (error) {
      const message = error.response?.data?.message || 'Yetki oluşturulamadı';
      alertify.error(message);
      return rejectWithValue(message);
    }
  }
);

/**
 * Update permission
 */
export const updatePermission = createAsyncThunk(
  'permission/updatePermission',
  async ({ permissionId, updates }, { rejectWithValue }) => {
    try {
      const permission = await permissionService.updatePermission(permissionId, updates);
      alertify.success('Yetki başarıyla güncellendi');
      return permission;
    } catch (error) {
      const message = error.response?.data?.message || 'Yetki güncellenemedi';
      alertify.error(message);
      return rejectWithValue(message);
    }
  }
);

/**
 * Delete permission
 */
export const deletePermission = createAsyncThunk(
  'permission/deletePermission',
  async (permissionId, { rejectWithValue }) => {
    try {
      await permissionService.deletePermission(permissionId);
      alertify.success('Yetki başarıyla silindi');
      return permissionId;
    } catch (error) {
      const message = error.response?.data?.message || 'Yetki silinemedi';
      alertify.error(message);
      return rejectWithValue(message);
    }
  }
);

/**
 * Get available permissions (for company owners)
 */
export const fetchAvailablePermissions = createAsyncThunk(
  'permission/fetchAvailablePermissions',
  async (_, { rejectWithValue }) => {
    try {
      return await permissionService.getAvailablePermissions();
    } catch (error) {
      const message = error.response?.data?.message || 'Mevcut yetkiler yüklenemedi';
      return rejectWithValue(message);
    }
  }
);

// ============= SLICE =============

const permissionSlice = createSlice({
  name: 'permission',
  initialState: {
    permissions: [],
    availablePermissions: [],
    isLoading: false,
    error: null
  },
  reducers: {
    clearPermissions: (state) => {
      state.permissions = [];
      state.availablePermissions = [];
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // ========== Fetch Permissions ==========
      .addCase(fetchPermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissions = action.payload;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // ========== Create Permission ==========
      .addCase(createPermission.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createPermission.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissions.push(action.payload);
      })
      .addCase(createPermission.rejected, (state) => {
        state.isLoading = false;
      })
      
      // ========== Update Permission ==========
      .addCase(updatePermission.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePermission.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.permissions.findIndex(p => p._id === action.payload._id);
        if (index !== -1) {
          state.permissions[index] = action.payload;
        }
      })
      .addCase(updatePermission.rejected, (state) => {
        state.isLoading = false;
      })
      
      // ========== Delete Permission ==========
      .addCase(deletePermission.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deletePermission.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissions = state.permissions.filter(p => p._id !== action.payload);
      })
      .addCase(deletePermission.rejected, (state) => {
        state.isLoading = false;
      })
      
      // ========== Fetch Available Permissions ==========
      .addCase(fetchAvailablePermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAvailablePermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.availablePermissions = action.payload;
      })
      .addCase(fetchAvailablePermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

export const { clearPermissions } = permissionSlice.actions;
export default permissionSlice.reducer;

